import { useState, useEffect, useCallback } from "react";

// Simple persistent form state hook for dashboard components
export function usePersistentFormState<T>(
  initialValue: T,
  storageKey: string,
  sectionId?: string
): [T, (value: T) => void, () => void] {
  const fullKey = sectionId ? `${sectionId}_${storageKey}` : storageKey;

  // Get initial value from storage or use provided initial value
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = sessionStorage.getItem(fullKey);
      if (stored !== null) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.warn("Failed to parse stored value for", fullKey, error);
    }
    return initialValue;
  });

  // Update storage when value changes
  useEffect(() => {
    try {
      sessionStorage.setItem(fullKey, JSON.stringify(value));
    } catch (error) {
      console.warn("Failed to store value for", fullKey, error);
    }
  }, [value, fullKey]);

  // Reset function
  const resetValue = useCallback(() => {
    setValue(initialValue);
    try {
      sessionStorage.removeItem(fullKey);
    } catch (error) {
      console.warn("Failed to remove stored value for", fullKey, error);
    }
  }, [initialValue, fullKey]);

  return [value, setValue, resetValue];
}
